import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';

import { METER_CONTENT } from '../content/meter-content';
import { useSelector, useSetUsage, useSetUsageType } from '../context/hooks';
import { selectMeter, selectUsage, selectUsageType } from '../context/selectors';
import {
	useUbpStep2BackClickedEvent,
	useUbpStep2GetEstimateClickedEvent,
	useUbpStep2ScreenViewedEvent,
	useUbpUsageProfileSelectedEvent,
} from '../ubp-calculator-analytics';

import { ChoiceCard } from './choice-card';
import { NumericInput } from './inputs/numeric-input';
import { StepNav } from './step-nav';
import { Stepper } from './stepper';
import { METER_LABELS } from './strings';

import type { BulletOrIconListItem } from './strings';
import type { UsageType } from '../context/types';

type NeedsEntry = {
	label: string;
	description: string | ReactNode;
	usageRange?: string;
	info?: string;
	list?: BulletOrIconListItem[];
};

/**
 * Hover / focus controlled help-tooltip used on the "Do you know your
 * usage?" heading (Figma nodes 2023:11772 + 1843:3742).
 *
 * Why not `@atlaskit/tooltip`?
 * We initially wired this up with `@atlaskit/tooltip`, which is the
 * WAC-blessed pattern for hover help elsewhere in the codebase. Inside
 * the usage-based-pricing calculator shell it consistently rendered its
 * portalled bubble at `top: ~1500px, left: 0` — hundreds of pixels
 * below the anchor button — regardless of `position`, `strategy`, or a
 * custom `component` override. Instrumented onShow/onHide/mouseenter/
 * mouseleave logs confirmed the show event was firing and the portal
 * was mounting at document.body, but the underlying `@atlaskit/popper`
 * position calculation was silently defaulting to (0, viewportBottom).
 * The root cause is likely something specific about the calculator
 * shell's stacking / ancestor CSS interaction with atlaskit's popper,
 * but we couldn't isolate it after several rounds of debugging.
 *
 * This custom implementation gives us:
 *   - reliable positioning (absolute, relative to the anchor span)
 *   - pixel-perfect Figma-spec styling (black bg, 20px radius,
 *     16px padding, Charlie Text 14/20, elevation shadow)
 *   - the same a11y story (`role="tooltip"` on the bubble,
 *     `aria-label` on the trigger button, focus + hover parity)
 *   - no portal / no popper / no atlaskit dependency for this element
 *
 * If we later find a fix for the atlaskit positioning issue we can
 * revert to `<Tooltip>` — the API surface here is intentionally minimal
 * (`label` + `text`) so the swap is a small drop-in.
 */
const UsageHelpTooltip = ({ label, text }: { label: string; text: string }) => {
	const [visible, setVisible] = useState(false);
	return (
		<span css={helpTooltipAnchorStyle}>
			<button
				type="button"
				css={helpIconButtonStyle}
				aria-label={label}
				onMouseEnter={() => setVisible(true)}
				onMouseLeave={() => setVisible(false)}
				onFocus={() => setVisible(true)}
				onBlur={() => setVisible(false)}
			>
				<span aria-hidden="true" css={helpIconGlyphStyle}>
					?
				</span>
			</button>
			{visible && (
				<span css={helpTooltipBubbleStyle} role="tooltip">
					{text}
				</span>
			)}
		</span>
	);
};

export const NeedsStep = () => {
	const intl = useIntl();
	const usageType = useSelector(selectUsageType);
	const setUsageType = useSetUsageType();
	const meter = useSelector(selectMeter);
	const usage = useSelector(selectUsage);
	const setUsage = useSetUsage();

	const fireStep2Viewed = useUbpStep2ScreenViewedEvent();
	const fireUsageProfileSelected = useUbpUsageProfileSelectedEvent();
	const fireStep2GetEstimate = useUbpStep2GetEstimateClickedEvent();
	const fireStep2Back = useUbpStep2BackClickedEvent();

	useEffect(() => {
		fireStep2Viewed();
		// Intentionally run once on mount only.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const NEEDS_STRINGS = useMemo<Record<UsageType, NeedsEntry>>(
		() =>
			(Object.keys(METER_CONTENT[meter].usageProfiles) as UsageType[]).reduce(
				(acc, key) => {
					const profile = METER_CONTENT[meter].usageProfiles[key];
					acc[key] = {
						label: intl.formatMessage({
							id: `ubp.calculator.needs.${meter}.${key}.label`,
							defaultMessage: profile.label,
						}),
						description: intl.formatMessage({
							id: `ubp.calculator.needs.${meter}.${key}.description`,
							defaultMessage: profile.description,
						}),
						usageRange: profile.usageRange
							? intl.formatMessage({
									id: `ubp.calculator.needs.${meter}.${key}.usageRange`,
									defaultMessage: profile.usageRange,
								})
							: undefined,
						info: profile.valueProp
							? intl.formatMessage({
									id: `ubp.calculator.needs.${meter}.${key}.valueProp`,
									defaultMessage: profile.valueProp,
								})
							: undefined,
						list: profile.list?.map((text, idx) => ({
							text: intl.formatMessage({
								id: `ubp.calculator.needs.${meter}.${key}.list.${idx}`,
								defaultMessage: text,
							}),
						})),
					};
					return acc;
				},
				{} as Record<UsageType, NeedsEntry>,
			),
		[intl, meter],
	);

	const needsTypes = Object.keys(NEEDS_STRINGS) as UsageType[];

	return (
		<TypographyStyles>
			<Stepper visibleStepCount={3} />
			<h3 css={pageTitleStyle}>
				{intl.formatMessage({
					id: 'ubp.calculator.needs.pageTitle',
					defaultMessage: 'Tell us about your team\u2019s usage',
				})}
			</h3>
			<p css={pageSubtitleStyle} id="needs-step-heading">
				{intl.formatMessage(
					{
						id: 'ubp.calculator.needs.pageSubtitle',
						defaultMessage:
							'Choose one option that best describes how your team will use {meterLabel} each month',
					},
					{ meterLabel: METER_LABELS[meter] },
				)}
			</p>
			<div css={cardGridStyle} role="radiogroup" aria-labelledby="needs-step-heading">
				{needsTypes.map((choice: UsageType) => {
					const { label, description, list, info } = NEEDS_STRINGS[choice];
					return (
						<ChoiceCard
							key={`needs-${choice}`}
							value={choice}
							selected={usage === 0 && usageType === choice}
							onSelect={() => {
								setUsageType(choice);
								setUsage(0);
								fireUsageProfileSelected(choice);
							}}
							groupName="group-needs"
							label={label}
							description={description}
							list={list}
							info={info}
						/>
					);
				})}
			</div>

			<div css={orCss}>
				<div css={orLabel}>
					{intl.formatMessage({
						id: 'ubp.calculator.needs.usage.or',
						defaultMessage: 'Or',
					})}
				</div>
			</div>

			{/*
			 * Meter-aware usage question — sourced from the atomic content
			 * registry (`needsStep.doYouKnowQuestion`). The unit noun
			 * (credits / steps / resolutions / objects) differs per meter,
			 * so message IDs are scoped per meter to allow full-sentence
			 * translation without token interpolation.
			 */}
			<div css={usageQuestionStyle}>
				<div css={usageQuestionRowStyle}>
					<h3 css={usageQuestionHeadingStyle} id="usage-label">
						<label htmlFor="usage">
							{intl.formatMessage({
								id: `ubp.calculator.needs.${meter}.doYouKnowQuestion`,
								defaultMessage: METER_CONTENT[meter].needsStep.doYouKnowQuestion,
							})}
						</label>
					</h3>
					{/*
					 * ? icon with hover / focus tooltip per Figma nodes
					 * 2023:11772 + 1843:3742. See `UsageHelpTooltip` above
					 * for why this uses a custom implementation instead of
					 * `@atlaskit/tooltip`.
					 */}
					<UsageHelpTooltip
						label={intl.formatMessage({
							id: 'ubp.calculator.needs.usageQuestionTooltipLabel',
							defaultMessage: 'More information about usage',
						})}
						text={intl.formatMessage({
							id: 'ubp.calculator.needs.usageQuestionTooltip',
							defaultMessage: 'Your estimated monthly usage for each meter',
						})}
					/>
				</div>
				<div css={inputAndOptionalStyle}>
					<NumericInput
						id="usage"
						value={usage}
						min={0}
						// Narrower than the default 160 so the H3 question
						// (which for meters like "Automation steps" is
						// visibly longer than the Rovo credits variant) has
						// more room on the shared row before wrapping.
						width={120}
						aria-labelledby="usage-label"
						onChange={(next) => setUsage(next ?? 0)}
					/>
					<span css={optionalLabelStyle}>
						{intl.formatMessage({
							id: 'ubp.calculator.needs.overrides',
							defaultMessage: 'Overrides any profile selected above',
						})}
					</span>
				</div>
			</div>
			<StepNav
				showBack
				showNext
				nextLabel={METER_CONTENT[meter].needsStep.nextCta ?? 'Get estimate'}
				onBeforeNext={fireStep2GetEstimate}
				onBeforeBack={fireStep2Back}
			/>
		</TypographyStyles>
	);
};

// Page title — rendered as an H3 (Charlie Display Bold 32/38 via
// TypographyStyles). Left-aligned per Figma; 40px top padding keeps the title
// spaced from the Stepper, and the subtitle below owns the bottom spacing so
// the title + subtitle stay visually grouped.
const pageTitleStyle = css({
	margin: 0,
	paddingBlockStart: '40px',
});

// Body Large subtitle beneath the H3 title. Charlie Text 20/28 with the
// standard neutral text color. Sits directly under the title with a small gap
// above (space.100) and 40px padding below to separate the title/subtitle
// pair from the choice-card grid. The visible copy dynamically references the
// meter chosen in the previous step (e.g. "Rovo Credits", "Automations", "CS
// Resolutions", "Assets Objects") via ICU `{meterLabel}` interpolation.
const pageSubtitleStyle = css({
	margin: 0,
	paddingBlockStart: token('space.100'),
	paddingBlockEnd: '40px',
	fontSize: '1.25rem',
	lineHeight: '1.75rem',
	fontWeight: 400,
	color: wacToken('color.neutral.light1100'),
});

const cardGridStyle = css({
	display: 'grid',
	gap: token('space.400'),
	gridTemplateColumns: '1fr 1fr 1fr',
	// Mobile viewport (< 768px, atlaskit's `below.md`) stacks the three
	// choice cards into a single column per Figma mobile node 1160:27616.
	// We skip the intermediate 2-column layout since it only ever showed
	// briefly (768–480px) and doesn't match any Figma frame.
	[media.below.sm]: {
		gap: token('space.300'),
		gridTemplateColumns: '1fr',
	},
});

const orCss = css({
	padding: `${token('space.200')} 0 0`,
	overflow: 'hidden',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});

const orLabel = css({
	fontSize: '0.875rem',
	letterSpacing: '0.0625rem',
	position: 'relative',
	color: wacToken('color.neutral.light700'),
	padding: token('space.200'),
	textTransform: 'uppercase',
	['&:before, &:after']: {
		content: '',
		insetBlockStart: 0,
		insetBlockEnd: 0,
		position: 'absolute',
		width: '800px',
		height: '1px',
		margin: 'auto',
		background: wacToken('color.neutral.light300'),
	},
	['&:before']: {
		insetInlineEnd: '100%',
	},
	['&:after']: {
		insetInlineStart: '100%',
	},
});

// Wraps the usage-question H3 and the input + "Optional" indicator so they sit
// on the same row — mirrors usage-step.tsx's orgSizeQuestionStyle so both steps
// share the same input row treatment. 60px top padding lives on the row
// wrapper so both sides shift together and stay vertically aligned.
const usageQuestionStyle = css({
	display: 'flex',
	flexDirection: 'row',
	alignItems: 'center',
	gap: token('space.200'),
	flexWrap: 'wrap',
	paddingBlockStart: `${token('space.400')}`,
	paddingBlockEnd: `${token('space.500')}`,
});

// Places the H3 label and the `?` help icon side-by-side, vertically
// centered on the label baseline. Used to attach the tooltip trigger
// inline with the question heading per Figma node 2023:11772.
const usageQuestionRowStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.100'),
});

// Bare button wrapper for the `?` icon per Figma node 2023:11772.
// We render a solid black circle with a white `?` glyph via a plain
// text character (not an SVG icon) to avoid conflicts with atlaskit
// icon strokes. The button uses a real <button> (not a <span>) so
// it's keyboard-focusable and meets WCAG for interactive UI — it's
// the tooltip trigger inside `UsageHelpTooltip`.
const helpIconButtonStyle = css({
	appearance: 'none',
	border: 'none',
	padding: 0,
	margin: 0,
	font: 'inherit',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	cursor: 'pointer',
	pointerEvents: 'auto',
	// Solid black circle background per Figma node 1843:3742.
	backgroundColor: wacToken('color.neutral.light1100'),
	width: '20px',
	height: '20px',
	borderRadius: '50%',
	color: wacToken('color.neutral.light0'),
	'&:hover': {
		backgroundColor: wacToken('color.neutral.light1000'),
	},
	'&:focus-visible': {
		outline: `2px solid ${wacToken('color.primary.blue')}`,
		outlineOffset: '2px',
	},
});

// Plain `?` glyph — bold, tight line-height so it centers cleanly inside the
// 20×20 button without any built-in icon stroke.
const helpIconGlyphStyle = css({
	fontSize: '14px',
	fontWeight: 700,
	lineHeight: 1,
	fontFamily: wacToken('font.charlie.text'),
});

// Positions the ? button relative to its tooltip bubble so the bubble
// can absolutely-position itself above/beside the button.
const helpTooltipAnchorStyle = css({
	position: 'relative',
	display: 'inline-flex',
});

// Tooltip bubble per Figma node 1843:3742. Rendered as an
// absolutely-positioned <span> above the `?` button (no portal, no
// popper). See the doc comment on `UsageHelpTooltip` above for why we
// went custom instead of `@atlaskit/tooltip`.
const helpTooltipBubbleStyle = css({
	position: 'absolute',
	insetBlockEnd: 'calc(100% + 8px)',
	insetInlineStart: '50%',
	transform: 'translateX(-50%)',
	minWidth: '200px',
	maxWidth: '320px',
	padding: '16px',
	borderRadius: '20px',
	backgroundColor: wacToken('color.neutral.light1100'),
	color: wacToken('color.neutral.light0'),
	fontSize: '14px',
	lineHeight: '20px',
	fontFamily: wacToken('font.charlie.text'),
	textAlign: 'left',
	boxShadow: token('elevation.shadow.overlay'),
	zIndex: 100,
	pointerEvents: 'none',
});
const usageQuestionHeadingStyle = css({
	margin: 0,
});

// Places the number input and the "Optional" indicator side-by-side, with the
// label vertically centered on the input.
const inputAndOptionalStyle = css({
	display: 'flex',
	alignItems: 'center',
	gap: token('space.200'),
});

const optionalLabelStyle = css({
	fontSize: '0.875rem',
	lineHeight: '20px',
	color: wacToken('color.neutral.light1100'),
});
